import { Link } from "@tanstack/react-router";
import type { SequenceTriggerType } from "@unbox/api";
import { Route } from "@untitledui/icons";
import { useState } from "react";
import {
  useCollaborationSequenceOverrides,
  useDeleteCollaborationSequenceOverride,
  useUpsertCollaborationSequenceOverride,
} from "@/api/collaborations";
import { useSequenceDefaults } from "@/api/sequences";
import { Button } from "@/components/base/buttons/button";
import { Toggle } from "@/components/base/toggle/toggle";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useCollabTriggerTypes } from "@/pages/sequences/collab-trigger-types";
import { SequenceEditorDialog } from "@/pages/sequences/sequence-editor-dialog";
import { TRIGGER_LABELS } from "@/pages/sequences/sequence-steps-preview";
import { useMerchant } from "@/providers/MerchantProvider";
import { cx } from "@/utils/cx";

type Mode = "default" | "custom";

function ModePill({ value, onChange, disabled }: { value: Mode; onChange: (next: Mode) => void; disabled: boolean }) {
  const base =
    "px-3 py-1 text-xs font-medium rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-60";
  const active = "bg-brand-solid text-primary_on-brand";
  const inactive = "text-tertiary hover:text-secondary";
  return (
    <div className={cx("inline-flex items-center rounded-full bg-secondary p-0.5", disabled && "opacity-60")}>
      <button
        type="button"
        className={cx(base, value === "default" ? active : inactive)}
        onClick={() => value !== "default" && onChange("default")}
        disabled={disabled}
      >
        Default
      </button>
      <button
        type="button"
        className={cx(base, value === "custom" ? active : inactive)}
        onClick={() => value !== "custom" && onChange("custom")}
        disabled={disabled}
      >
        Custom
      </button>
    </div>
  );
}

function OverrideRow({
  collaborationId,
  collaborationName,
  triggerType,
  override,
  globalDefault,
}: {
  collaborationId: string;
  collaborationName: string;
  triggerType: SequenceTriggerType;
  override: { enabled: boolean; sequenceId: string | null; sequenceName: string | null } | null;
  globalDefault: { sequenceId: string; sequenceName: string } | null;
}) {
  const upsert = useUpsertCollaborationSequenceOverride();
  const deleteOverride = useDeleteCollaborationSequenceOverride();
  const [editOpen, setEditOpen] = useState(false);
  const [confirmRevertOpen, setConfirmRevertOpen] = useState(false);

  const isEnabled = override ? override.enabled : true;
  const hasCustomOverride = !!override?.sequenceId;
  const hasGlobalDefault = !!globalDefault;
  const hasAnySequence = hasCustomOverride || hasGlobalDefault;

  const statusText = !isEnabled
    ? "Disabled for this collaboration"
    : hasCustomOverride
      ? "Custom override for this collab"
      : hasGlobalDefault
        ? "Global default"
        : "No sequence configured yet.";

  const handleToggle = (enabled: boolean) => {
    upsert.mutate({
      collaborationId,
      triggerType,
      enabled,
      sequenceId: override?.sequenceId ?? null,
    });
  };

  const handleModeChange = (next: Mode) => {
    if (next === "custom") {
      // Default → Custom: open the editor to create an override, seeded from the global.
      setEditOpen(true);
    } else {
      // Custom → Default: destructive (deletes the override). Confirm first.
      setConfirmRevertOpen(true);
    }
  };

  // When editing: if a custom override exists, edit it directly. Otherwise seed a new sequence
  // from the global's steps (if a global exists) or start empty.
  const sequenceIdToEdit = override?.sequenceId ?? null;
  const seedFromSequenceId = override?.sequenceId ? null : (globalDefault?.sequenceId ?? null);

  const dimmed = !isEnabled;
  // Show the Default | Custom pill only when a global default exists (so "Default" has meaning).
  const showModePill = hasGlobalDefault;
  // Hide the button while on "Default" mode — the pill's Custom click opens the editor for us.
  // Show "Create" when nothing is configured yet, or "Configure" to edit the existing override.
  const showActionButton = !hasAnySequence || hasCustomOverride;
  const buttonLabel = hasCustomOverride ? "Configure" : "Create";

  return (
    <div className="flex flex-col rounded-lg border border-secondary bg-primary">
      <div className="flex items-center justify-between p-4">
        <p className={cx("text-sm font-semibold", dimmed ? "text-quaternary" : "text-primary")}>
          {TRIGGER_LABELS[triggerType] ?? triggerType}
        </p>
        <Toggle isSelected={isEnabled} onChange={handleToggle} />
      </div>

      <div className="border-t border-secondary" />

      <div className="flex items-center justify-between gap-3 p-4">
        <p className={cx("text-sm truncate", dimmed ? "italic text-quaternary" : "text-tertiary")}>{statusText}</p>
        <div className="flex items-center gap-2 shrink-0">
          {showModePill && (
            <ModePill
              value={hasCustomOverride ? "custom" : "default"}
              onChange={handleModeChange}
              disabled={!isEnabled || upsert.isPending || deleteOverride.isPending}
            />
          )}
          {showActionButton && (
            <Button color="secondary" size="sm" onClick={() => setEditOpen(true)} isDisabled={!isEnabled}>
              {buttonLabel}
            </Button>
          )}
        </div>
      </div>

      <SequenceEditorDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        triggerType={triggerType}
        scope={collaborationName}
        sequenceId={sequenceIdToEdit}
        seedFromSequenceId={seedFromSequenceId}
        onSaved={(savedId, created) => {
          if (created) {
            upsert.mutate({ collaborationId, triggerType, enabled: true, sequenceId: savedId });
          }
        }}
      />

      <Dialog open={confirmRevertOpen} onOpenChange={setConfirmRevertOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Revert to global default?</DialogTitle>
            <DialogDescription>
              This will delete the custom override for this collaboration. Future runs will use the global default
              sequence. Runs already in progress are unaffected.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button color="secondary" size="sm" onClick={() => setConfirmRevertOpen(false)}>
              Cancel
            </Button>
            <Button
              color="primary-destructive"
              size="sm"
              isLoading={deleteOverride.isPending}
              onClick={async () => {
                await deleteOverride.mutateAsync({ collaborationId, triggerType });
                setConfirmRevertOpen(false);
              }}
            >
              Revert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function CollaborationSequenceOverrides({
  collaborationId,
  collaborationName,
}: {
  collaborationId: string;
  collaborationName: string;
}) {
  const { data: overrides = [] } = useCollaborationSequenceOverrides(collaborationId);
  const { data: defaults = [] } = useSequenceDefaults();
  const [open, setOpen] = useState(false);
  const merchantId = useMerchant();
  const triggerTypes = useCollabTriggerTypes();

  const overrideMap = new Map(overrides.map((o) => [o.triggerType, o]));
  const defaultMap = new Map(defaults.map((d) => [d.triggerType, d]));

  return (
    <>
      <Button color="secondary" size="md" iconLeading={<Route className="w-4 h-4" />} onClick={() => setOpen(true)}>
        Automations
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between gap-3 pr-4">
              <DialogTitle>Automation triggers</DialogTitle>
              <Link
                to="/merchant/$merchantId/automate-outreach"
                params={{ merchantId }}
                className="text-sm text-brand-secondary hover:text-brand-tertiary underline"
              >
                Edit global defaults
              </Link>
            </div>
            <DialogDescription>Configure what runs when each event fires.</DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-3">
            {triggerTypes.map((triggerType) => (
              <OverrideRow
                key={triggerType}
                collaborationId={collaborationId}
                collaborationName={collaborationName}
                triggerType={triggerType}
                override={overrideMap.get(triggerType) ?? null}
                globalDefault={defaultMap.get(triggerType) ?? null}
              />
            ))}
          </div>

          <DialogFooter>
            <Button color="secondary" size="sm" onClick={() => setOpen(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
